/**
 * The customizer panel: the base-cat switcher, the coat palette and pattern
 * chips, every slider in schema.js's GROUPS, save/randomise/reset, and the
 * saved-cats shelf.  Built entirely in JS and mounted into the empty
 * #customizer div, with its CSS injected from here as one <style> --
 * index.html stays ignorant of the panel, so the page and the panel cannot
 * version-skew.
 *
 * The one real hazard is event leakage.  main.js listens on *window* for
 * keydown/keyup (WASD, Space, Ctrl, digits), mousemove (look) and wheel
 * (zoom), and the canvas grabs pointer lock on mousedown.  Unstopped, typing
 * a cat's name walks the cat across the level and scrolling the panel zooms
 * the camera, so every bubbling event the game consumes is stopped at the
 * panel root.  Two deliberate exceptions:
 *
 *  - mouseup is NOT stopped: main.js clears its drag-look flag on window
 *    mouseup, and a canvas drag released over the panel would otherwise
 *    leave the camera glued to the mouse forever.
 *  - mousemove is not stopped: main.js ignores it unless pointer-locked or
 *    mid-canvas-drag, and neither can begin on the panel (mousedown never
 *    leaves it), so there is nothing to leak.
 *
 * The panel is a sibling of the canvas, not a child, so it can never be the
 * pointer-lock click target; opening it also *exits* any held lock, because
 * a dialog without a cursor is unusable.
 */
import { GROUPS, BASES, COATS, PATTERNS, defaultLook, normalize, randomLook } from "@catworld/shared";
import { listLooks, saveLook, deleteLook } from "./store.js";

/** Bubbling events the game binds on window, stopped at the panel root.
    keydown is handled separately (it also drives Escape-to-close). */
const ISOLATE = ["keyup", "pointerdown", "mousedown", "wheel", "touchstart"];

/** Display names for BASES keys; index.html's switcher spells them this way. */
const BASE_LABELS = {
  sandy: "Sandy", ginger: "Ginger", gray: "Grey", toon: "Toon", "toon-tabby": "Tabby",
};

const STYLE_ID = "customizer-style";

/* Everything is the page's own tokens so the panel reads as a sibling of
   #hud and #keys: same .panel chrome, same .6rem/.19em micro-labels, accent
   reserved for the title and the active states.  z-index 8 clears #debug (6)
   and #debug-toast (7).  Right edge because #hud owns top-left, #keys
   bottom-left, and the cat owns the centre; max-height + its own scroll keeps
   a laptop screen from clipping the Eyes group.  The closed state keeps
   visibility:hidden (delayed past the fade, #charge's trick) so a closed
   panel can never eat a click meant for the canvas. */
const CSS = `
.cz{top:16px;right:16px;width:302px;z-index:8;padding:14px 16px 16px;
  display:flex;flex-direction:column;gap:10px;
  max-height:calc(100vh - 32px);overflow-y:auto;overscroll-behavior:contain;
  scrollbar-width:thin;outline:none;
  opacity:0;visibility:hidden;transform:translateX(14px);
  transition:opacity .18s ease,transform .18s ease,visibility 0s .18s}
.cz.open{opacity:1;visibility:visible;transform:none;
  transition:opacity .15s ease,transform .15s ease}
.cz-head{display:flex;align-items:center;justify-content:space-between}
.cz-title{font-size:.6rem;letter-spacing:.19em;text-transform:uppercase;
  color:var(--accent)}
.cz-h{font-size:.6rem;letter-spacing:.19em;text-transform:uppercase;
  color:var(--dim);margin-top:5px}
.cz-row{display:grid;grid-template-columns:92px 1fr 44px;gap:8px;align-items:center}
.cz-row label{font-size:.66rem;color:var(--dim);letter-spacing:.03em}
.cz-val{font-size:.66rem;color:var(--ink);text-align:right;
  font-variant-numeric:tabular-nums;white-space:nowrap}
.cz input[type=range]{appearance:none;-webkit-appearance:none;width:100%;
  height:14px;margin:0;background:transparent;cursor:pointer}
.cz input[type=range]::-webkit-slider-runnable-track{height:3px;border-radius:2px;
  background:var(--edge)}
.cz input[type=range]::-webkit-slider-thumb{-webkit-appearance:none;width:11px;
  height:11px;margin-top:-4px;border-radius:50%;border:0;background:var(--accent)}
.cz input[type=range]::-moz-range-track{height:3px;border-radius:2px;
  background:var(--edge)}
.cz input[type=range]::-moz-range-thumb{width:11px;height:11px;border-radius:50%;
  border:0;background:var(--accent)}
.cz input[type=range]:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
/* The coat palette.  Chips are the colour itself -- a labelled list of
   colour NAMES would make the player translate "lilac" in their head, and
   the whole point of the grid is that the eye picks.  Seven columns puts
   the thirteen swatches on two rows at ~33px a chip: big enough that the
   dark browns are tellable apart, small enough that the grid reads as one
   control and not a second panel.  Selection is the accent outline the
   sliders' focus rings already use, NOT a background change -- the
   background IS the datum here and must never be repainted by state. */
.cz-sw-grid{display:grid;grid-template-columns:repeat(7,1fr);gap:6px}
.cz-sw{aspect-ratio:1/1;padding:0;border:1px solid var(--edge);border-radius:4px;
  cursor:pointer;transition:border-color .15s}
.cz-sw:hover{border-color:rgba(241,239,232,.45)}
.cz-sw.on{outline:2px solid var(--accent);outline-offset:1px;border-color:var(--accent)}
.cz-sw:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
/* "Natural" has no one colour -- it is whichever coat the base cat shipped
   in -- so its chip is a slice of all four shipped coats.  A blank or
   crossed-out chip read as "no colour", which players took to mean the cat
   would go grey. */
.cz-sw-nat{background:conic-gradient(from 45deg,#c99a63,#b8b4ad,#8d8a85,#c9803d,#c99a63)}
/* The pattern chips reuse .switch's button chrome (same border, same accent
   .on state as the base-cat row) but trade its one-row flex for a grid:
   eight patterns in one row would be eight unreadable slivers.  The .cz
   ancestor is load-bearing, not tidiness: the standalone build relocates the
   page's stylesheet into <body>, BELOW this injected <style>, so a plain
   .cz-pat ties .switch on specificity and loses on document order -- the
   panel came up flex in dist and grid in dev, which is exactly the
   version-skew this file exists to prevent. */
.cz .cz-pat{display:grid;grid-template-columns:repeat(4,1fr)}
.cz-name-row{display:flex;gap:6px}
.cz-name-row input{flex:1;min-width:0;font-family:inherit;font-size:.72rem;
  color:var(--ink);background:transparent;border:1px solid var(--edge);
  border-radius:3px;padding:6px 8px}
.cz-name-row input::placeholder{color:var(--dim);opacity:.7}
.cz-name-row input:focus{outline:none;border-color:var(--accent)}
.cz-status{font-size:.62rem;letter-spacing:.05em;color:var(--dim);min-height:1em}
.cz-saved{display:flex;flex-direction:column;gap:6px}
.cz-saved-row{display:flex;gap:6px;align-items:stretch}
.cz-load{flex:1;min-width:0;text-align:left;font-family:inherit;font-size:.7rem;
  letter-spacing:.03em;padding:6px 9px;color:var(--ink);background:transparent;
  border:1px solid var(--edge);border-radius:3px;cursor:pointer;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;
  transition:color .15s,border-color .15s}
.cz-load:hover{color:var(--accent);border-color:var(--accent)}
.cz-load:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.cz-x{flex:0 0 auto;font-family:inherit;font-size:.72rem;line-height:1;
  padding:5px 8px;color:var(--dim);background:transparent;
  border:1px solid var(--edge);border-radius:3px;cursor:pointer;
  transition:color .15s,border-color .15s}
.cz-x:hover{color:var(--ink);border-color:rgba(241,239,232,.32)}
.cz-x:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
.cz-none{font-size:.66rem;color:var(--dim)}
@media (prefers-reduced-motion:reduce){
  .cz{transform:none;transition:opacity .12s,visibility 0s .12s}
  .cz.open{transition:opacity .12s}
}
@media (max-width:640px){.cz{width:calc(100vw - 32px)}}
/* Thumbs, for thumbs.  An 11 px handle is a comfortable mouse target and a
   poor finger one -- the contact patch of a fingertip is nearer 40 px, so the
   handle disappears underneath it and the drag becomes a guess.  20 px on a
   26 px row is still small against that patch, but it is enough to see what
   you have hold of, and it does not reflow the panel: the row grows by 12 px
   and twenty rows of that is one scroll's worth on a panel that already
   scrolls.  The track stays 3 px; tapping it jumps the value, which is the
   fast way to set a slider with a thumb anyway. */
@media (pointer:coarse){
  .cz input[type=range]{height:26px}
  .cz input[type=range]::-webkit-slider-thumb{width:20px;height:20px;margin-top:-8px}
  .cz input[type=range]::-moz-range-thumb{width:20px;height:20px}
}
`;

function injectStyle() {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

/** createElement + textContent only.  Saved names are user text and must
    never travel through innerHTML. */
function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(className, text, ariaLabel, onClick) {
  const b = el("button", className, text);
  b.type = "button";
  b.setAttribute("aria-label", ariaLabel);
  b.addEventListener("click", onClick);
  return b;
}

function fmt(p, v) {
  return p.fmt ? p.fmt(v) : String(v);
}

export class CustomizerPanel {
  constructor({ mount, onChange, onBase, initial }) {
    this.onChange = onChange || (() => {});
    this.onBase = onBase || (() => {});
    this.look = normalize(initial);
    this.opened = false;
    this.controls = new Map();       // param key    -> { input, val, p }
    this.baseButtons = new Map();    // base key     -> button
    this.coatButtons = new Map();    // coat key     -> swatch button
    this.patternButtons = new Map(); // pattern key  -> chip button
    injectStyle();
    this.root = this.build();
    mount.appendChild(this.root);
    // Escape must close the panel even when focus never entered it (the
    // player opened it, then clicked back onto the game).  Document-level and
    // Escape-only, so every other key still belongs to whoever has focus.
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.opened) this.close();
    }, true);
  }

  build() {
    const root = el("div", "panel cz");
    root.setAttribute("role", "dialog");
    root.setAttribute("aria-label", "Cat customizer");
    root.setAttribute("aria-hidden", "true");
    root.tabIndex = -1;             // focusable on open, so Escape lands here

    for (const type of ISOLATE) {
      root.addEventListener(type, (e) => e.stopPropagation());
    }
    root.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Escape") this.close();
    });

    // Header
    const head = el("div", "cz-head");
    head.append(
      el("div", "cz-title", "Customise cat"),
      button("cz-x", "✕", "Close customizer", () => this.close()),
    );
    root.appendChild(head);

    // Base cat
    root.appendChild(el("div", "cz-h", "Base cat"));
    const baseRow = el("div", "switch");
    for (const key of BASES) {
      const label = BASE_LABELS[key] || key;
      const b = button("", label, `Base cat: ${label}`, () => this.pickBase(key));
      this.baseButtons.set(key, b);
      baseRow.appendChild(b);
    }
    root.appendChild(baseRow);

    // Sliders, with the two picker grids ahead of their groups' sliders --
    // the swatch or chip is the coarse pick and the sliders below it the
    // refinement, so the controls sit in the order they are meant to be
    // used.  Keyed on the group title rather than on position so a group
    // added to the schema cannot silently inherit a picker.
    for (const group of GROUPS) {
      root.appendChild(el("div", "cz-h", group.title));
      if (group.title === "Coat") root.appendChild(this.buildSwatches());
      if (group.title === "Pattern") root.appendChild(this.buildPatternPicker());
      for (const p of group.params) root.appendChild(this.buildSlider(p));
    }

    // Name + actions
    root.appendChild(el("div", "cz-h", "Save this cat"));
    const nameRow = el("div", "cz-name-row");
    this.nameInput = el("input");
    this.nameInput.type = "text";
    this.nameInput.id = "cz-name";
    this.nameInput.maxLength = 40;
    this.nameInput.placeholder = "Name this cat";
    this.nameInput.autocomplete = "off";
    this.nameInput.setAttribute("aria-label", "Cat name");
    // The name is part of the look, not just a label on the save button: the
    // HUD reads it back, so it has to travel on every keystroke like a slider
    // does rather than only at save time.
    this.nameInput.addEventListener("input", () => {
      this.look.name = this.nameInput.value;
      this.onChange(this.look);
    });
    nameRow.appendChild(this.nameInput);
    root.appendChild(nameRow);

    const actions = el("div", "switch");
    actions.append(
      button("", "Save", "Save this look", () => this.save()),
      button("", "Random", "Randomise the look", () => this.adopt(randomLook())),
      button("", "Reset", "Reset every slider", () => this.reset()),
    );
    root.appendChild(actions);
    this.status = el("div", "cz-status", "");
    root.appendChild(this.status);

    // Saved cats
    root.appendChild(el("div", "cz-h", "Saved cats"));
    this.savedList = el("div", "cz-saved");
    root.appendChild(this.savedList);
    this.refreshSaved();

    this.reflect();
    return root;
  }

  buildSlider(p) {
    const row = el("div", "cz-row");
    const label = el("label", "", p.label);
    label.htmlFor = `cz-${p.key}`;
    const input = el("input");
    input.type = "range";
    input.id = `cz-${p.key}`;
    input.min = p.min;
    input.max = p.max;
    input.step = p.step;
    const val = el("span", "cz-val");
    input.addEventListener("input", () => {
      const v = Number(input.value);
      this.look[p.key] = v;
      // One cached-span text write per tick; a drag never rebuilds a row.
      val.textContent = fmt(p, v);
      this.onChange(this.look);
    });
    this.controls.set(p.key, { input, val, p });
    row.append(label, input, val);
    return row;
  }

  buildSwatches() {
    const grid = el("div", "cz-sw-grid");
    for (const c of COATS) {
      // The colour is the button's own background, and the name still
      // travels: aria-label for screen readers, title for the hover a
      // sighted player uses to learn that this brown is "seal".
      const b = button(c.hex ? "cz-sw" : "cz-sw cz-sw-nat", "",
                       `Coat colour: ${c.label}`, () => this.pickCoat(c.key));
      if (c.hex) b.style.background = c.hex;
      b.title = c.label;
      this.coatButtons.set(c.key, b);
      grid.appendChild(b);
    }
    return grid;
  }

  buildPatternPicker() {
    const grid = el("div", "switch cz-pat");
    for (const p of PATTERNS) {
      const b = button("", p.label, `Coat pattern: ${p.label}`, () => this.pickPattern(p.key));
      this.patternButtons.set(p.key, b);
      grid.appendChild(b);
    }
    return grid;
  }

  // ---- callbacks out --------------------------------------------------------

  pickBase(key) {
    if (key === this.look.base) return;
    this.look.base = key;
    this.reflectBase();
    this.onBase(key);
  }

  /** A swatch or a chip is a look edit like a slider drag -- same onChange,
      no special path -- so persistence, the wire and the HUD all see it for
      free.  Re-picking the current one is dropped for the same reason
      pickBase drops it: an onChange that changed nothing still writes
      storage and sends a frame. */
  pickCoat(key) {
    if (key === this.look.coat) return;
    this.look.coat = key;
    this.reflectCoat();
    this.onChange(this.look);
  }

  pickPattern(key) {
    if (key === this.look.pattern) return;
    this.look.pattern = key;
    this.reflectPattern();
    this.onChange(this.look);
  }

  /**
   * Show a base change the panel did not cause -- the HUD switcher, or the
   * debug number keys.  Reflect-only on purpose: firing onBase from here
   * would bounce back into the switch that called it, and re-reflecting the
   * whole look would clobber the sliders adopt() has just written.
   */
  showBase(key) {
    if (key === this.look.base) return;
    this.look.base = key;
    this.reflectBase();
  }

  /** Replace the whole look at once (random / reset / load a saved cat):
      reflect every control, then fire onBase *before* onChange so the morph
      lands on the right cat. */
  adopt(look) {
    const baseChanged = look.base !== this.look.base;
    this.look = look;
    this.reflect();
    if (baseChanged) this.onBase(look.base);
    this.onChange(this.look);
  }

  reset() {
    const look = defaultLook();
    look.base = this.look.base;     // resetting sliders should not swap the cat
    look.name = this.look.name;
    this.adopt(look);
  }

  save() {
    this.look.name = this.nameInput.value;
    const record = saveLook(this.nameInput.value, this.look);
    if (record) {
      // Echo the de-duplicated name back, so "Milo" quietly becoming
      // "Milo 2" is visible at the moment it happens.
      this.nameInput.value = record.name;
      this.look.name = record.name;
      this.status.textContent = `saved as “${record.name}”`;
      this.onChange(this.look);   // the echoed name is a change like any other
      this.refreshSaved();
    } else {
      this.status.textContent = "storage unavailable — not saved";
    }
  }

  // ---- reflecting state into the DOM ---------------------------------------

  /** Reflect an externally-set look into the controls.  No callbacks: the
      caller already has the look. */
  setLook(look) {
    this.look = normalize(look);
    this.reflect();
  }

  reflect() {
    for (const { input, val, p } of this.controls.values()) {
      input.value = this.look[p.key];
      val.textContent = fmt(p, this.look[p.key]);
    }
    this.nameInput.value = this.look.name || "";
    this.reflectBase();
    this.reflectCoat();
    this.reflectPattern();
  }

  reflectBase() {
    for (const [key, b] of this.baseButtons) {
      b.classList.toggle("on", key === this.look.base);
    }
  }

  reflectCoat() {
    for (const [key, b] of this.coatButtons) {
      b.classList.toggle("on", key === this.look.coat);
    }
  }

  reflectPattern() {
    for (const [key, b] of this.patternButtons) {
      b.classList.toggle("on", key === this.look.pattern);
    }
  }

  refreshSaved() {
    const rows = listLooks().map((r) => {
      const row = el("div", "cz-saved-row");
      // normalize() hands adopt a fresh object; adopting r.look itself would
      // alias it to this.look and let later slider drags edit the row's copy.
      row.append(
        button("cz-load", r.name, `Load ${r.name}`, () => this.adopt(normalize(r.look))),
        button("cz-x", "✕", `Delete ${r.name}`, () => {
          deleteLook(r.id);
          this.refreshSaved();
        }),
      );
      return row;
    });
    if (rows.length === 0) rows.push(el("div", "cz-none", "No saved cats yet"));
    this.savedList.replaceChildren(...rows);
  }

  // ---- open / close ---------------------------------------------------------

  open() {
    if (this.opened) return;
    this.opened = true;
    // Mouse-look may hold the pointer; a dialog without a cursor is unusable.
    document.exitPointerLock?.();
    this.root.classList.add("open");
    this.root.setAttribute("aria-hidden", "false");
    this.refreshSaved();            // another tab may have saved since last open
    this.root.focus({ preventScroll: true });
  }

  close() {
    if (!this.opened) return;
    this.opened = false;
    this.root.classList.remove("open");
    this.root.setAttribute("aria-hidden", "true");
    // Drop focus so the next keystroke is the game's again, not a hidden
    // slider's -- and so aria-hidden never hides the focused element.
    if (this.root.contains(document.activeElement)) document.activeElement.blur();
  }

  toggle() {
    this.opened ? this.close() : this.open();
  }

  get isOpen() {
    return this.opened;
  }
}
